import isEqual from 'react-fast-compare';
import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
} from 'src/components/Filters';
import {
  CatalogResults,
  DateTimeRange,
  DateTimeRangeWithRelative,
  EntityWithTicketsQuery,
  EntityWithTicketsQueryOperations,
  EventGroupingQuery,
  EventGroupingSortBy,
  EventGroupingType,
  EventSort,
  EventTimeFrameFilter,
  EventUnpricedStatus,
  EventWithData,
  FilterOperationType,
  InventoryViewMode,
  ListingQuery,
  ListingQueryOperations,
  ListingQueryV2,
  OnSaleEventQuery,
  OnSaleEventViewMode,
  PointOfSaleSaleStatus,
  PurchaseOrderQuery,
  PurchaseOrderQueryV2,
  PurchaseOrderState,
  PurchaseQueryOperations,
  PurchaseViewMode,
  ReportEntityWithTicketsQuery,
  SaleQuery,
  SaleQueryOperations,
  SaleQueryV2,
  SalesViewMode,
  UIReportCommissionQuery,
  UIReportV2CostOfGoodsSoldQuery,
  UIReportV2ListingQuery,
  UIReportV2PurchaseOrderLineItemQuery,
  UIReportV2PurchaseQuery,
  UIReportV2SaleLineItemQuery,
  UIReportV2SaleQuery,
  UIReportV2SaleTotalCostAdjustmentsQuery,
  UIReportV2SoldTicketQuery,
  UIReportV2TicketGroupQuery,
  UserSetting,
} from 'src/WebApiController';

import {
  DateRangePresetName,
  getLastDateRangeRelative,
  getNextDateRange,
  getUiDateTimeRangeFromPreset,
  PurchaseDateRangePresetNames,
} from './dateTimeUtils';

export enum EventMappingStateFilter {
  All = 'All',
  Mapped = 'Mapped',
  Pending = 'Pending',
  Rejected = 'Rejected',
}

export const DefaultEventGroupingQuery = {
  searchText: '',
  groupingType: EventGroupingType.Performer,
  sortBy: EventGroupingSortBy.Name,
  isSortDescending: false,
} satisfies EventGroupingQuery;

export type QueryWithViewMode = {
  timezoneOffsetMins: number | null;
  viewMode?: string | null;
};

export const VIEW_MODE_SETTING_ID_TO_DEFAULT = {
  [UserSetting.InventoryPageViewMode]: InventoryViewMode.TileView,
  [UserSetting.SalePageViewMode]: SalesViewMode.TileView,
  [UserSetting.PurchasePageViewMode]: PurchaseViewMode.EventTileView,
};

const DEFAULT_SEARCH_V2_PAGE_SIZE = 100;

export const EmptyEntityWithTicketsQuery = {
  entityIds: null,
  eventOrMappingIds: null,
  oldPosEventIds: null,
  marketplaceEntityIds: null,
  performerIds: null,
  venueIds: null,
  countryCodes: null,
  stateProvinceIds: null,
  cityIds: null,
  purchaseOrderId: null,
  purchaseOrderIds: null,
  saleIds: null,
  listingIds: null,
  purchaseVendorId: null,
  vendorAccessControlProviderId: null,
  purchaseVendorOrderId: null,
  purchaseVendorAccountIds: null,
  purchaseBuyerId: null,
  inHandDates: null,
  eventTimeFrameFilter: null,
  searchText: null,
  isBoosted: null,
  sortBy: null,
  isSortDescending: false,
  eventDates: null,
  eventNotReviewedSinceDate: null,
  sectionContains: null,
  sections: null,
  rows: null,
  sectionEquals: null,
  rowContains: null,
  rowEquals: null,
  sectionNotContains: null,
  rowNotContains: null,
  tagName: null,
  tagValue: null,
  eventTagName: null,
  eventTagValue: null,
  topLevelCategories: null,
  parentCategories: null,
  numberOfTicketsHeld: null,
  lastSaleDates: null,
  noSaleDates: null,
  isSeatSaver: null,
  timezoneOffsetMins: new Date().getTimezoneOffset(),
  purchaseDates: null,
  purchaseOrderCreatedBy: null,
  purchaseCommissionUserId: null,
  internalNotes: null,
  internalNotesContains: null,
  internalNotesEquals: null,
  internalNotesNotContains: null,
  currencyCode: null,
  eventStatuses: null,
  currencyCodes: null,
  searchConfigId: null,
  isMapped: null,
  isMappingRejected: null,
  purchaseVendorIds: null,
  purchaseBuyerIds: null,
  purchaseOrderCreatedBys: null,
  purchaseCommissionUserIds: null,
} satisfies EntityWithTicketsQuery;

export const DefaultEntityWithTicketsQueryOperations = {
  entityIdsOp: FilterOperationType.In,
  marketplaceEntityIdsOp: FilterOperationType.In,
  eventOrMappingIdsOp: FilterOperationType.In,
  oldPosEventIdsOp: FilterOperationType.In,
  performerIdsOp: FilterOperationType.In,
  venueIdsOp: FilterOperationType.In,
  countryCodesOp: FilterOperationType.In,
  stateProvinceIdsOp: FilterOperationType.In,
  cityIdsOp: FilterOperationType.In,
  purchaseOrderIdsOp: FilterOperationType.In,
  saleIdsOp: FilterOperationType.In,
  listingIdsOp: FilterOperationType.In,
  purchaseVendorIdOp: FilterOperationType.In,
  vendorAccessControlProviderIdOp: FilterOperationType.Equals,
  purchaseVendorAccountIdsOp: FilterOperationType.In,
  purchaseVendorOrderIdOp: FilterOperationType.Contains,
  purchaseBuyerIdOp: FilterOperationType.In,
  purchaseOrderCreatedByOp: FilterOperationType.In,
  purchaseCommissionUserIdOp: FilterOperationType.In,
  purchaseDatesOp: FilterOperationType.Between,
  inHandDatesOp: FilterOperationType.Between,
  eventTimeFrameFilterOp: FilterOperationType.Equals,
  sectionOp: FilterOperationType.Contains,
  rowOp: FilterOperationType.Contains,
  isSeatSaverOp: FilterOperationType.Equals,
  eventDatesOp: FilterOperationType.Between,
  eventNotReviewedSinceDateOp: FilterOperationType.GreaterThanOrEqual,
  isMappedOp: FilterOperationType.Equals,
  isMappingRejectedOp: FilterOperationType.Equals,
  searchTextOp: FilterOperationType.Contains,
  isBoostedOp: FilterOperationType.Equals,
  sortByOp: null,
  isSortDescendingOp: null,
  tagNameOp: FilterOperationType.Equals,
  tagValueOp: FilterOperationType.Contains,
  eventTagNameOp: FilterOperationType.Equals,
  eventTagValueOp: FilterOperationType.Contains,
  lastSaleDatesOp: FilterOperationType.GreaterThanOrEqual,
  noSaleDatesOp: FilterOperationType.GreaterThanOrEqual,
  numberOfTicketsHeldOp: FilterOperationType.Between,
  topLevelCategoriesOp: FilterOperationType.In,
  parentCategoriesOp: FilterOperationType.In,
  internalNotesOp: FilterOperationType.Contains,
  currencyCodesOp: FilterOperationType.In,
  eventStatusesOp: FilterOperationType.In,
  searchConfigIdOp: null,
} satisfies EntityWithTicketsQueryOperations;

export const DefaultListingQueryOperations = {
  ...DefaultEntityWithTicketsQueryOperations,
  pricerSellerUserIdOp: FilterOperationType.In,
  isPredeliveredOp: FilterOperationType.Equals,
  isListedOp: FilterOperationType.Equals,
  deliveryTypeOp: FilterOperationType.In,
  eventHasUnsoldTicketsOp: FilterOperationType.Equals,
  hasUnsoldTicketsOp: FilterOperationType.Equals,
  hasBroadcastErrorOp: FilterOperationType.Equals,
  isAutoPricingEnabledOp: FilterOperationType.Equals,
  isPricedAboveZeroOp: FilterOperationType.Equals,
  lastPriceUpdateDatesOp: FilterOperationType.Between,
  autoPricingResultTypesOp: FilterOperationType.In,
  isSeatingUnmappedOp: FilterOperationType.Equals,
  isAdminHoldOp: FilterOperationType.Equals,
  isDuplicateOp: FilterOperationType.Equals,
  isPricedByMarketplaceOp: FilterOperationType.Equals,
  isPendingProofOp: FilterOperationType.Equals,
  purchaseCancellationDatesOp: FilterOperationType.Between,
  purchaseCancelledOnlyOp: FilterOperationType.Equals,
  isNoFulfillOp: FilterOperationType.Equals,
  ticketTypeOp: FilterOperationType.In,
  splitTypeOp: FilterOperationType.In,
  eventUnpricedStatusOp: FilterOperationType.In,
} satisfies ListingQueryOperations;

export const DefaultSaleQueryOperations = {
  ...DefaultEntityWithTicketsQueryOperations,
  soldOnMarketplaceOp: FilterOperationType.In,
  saleStatusesOp: FilterOperationType.In,
  fulfillmentStatesOp: FilterOperationType.In,
  paymentStateOp: FilterOperationType.In,
  marketplacePaymentStateOp: FilterOperationType.In,
  paymentReceivedOp: FilterOperationType.Equals,
  paymentReferenceIdOp: FilterOperationType.Contains,
  paymentDatesOp: FilterOperationType.Between,
  fulfillmentDatesOp: FilterOperationType.Between,
  cancellationDatesOp: FilterOperationType.Between,
  saleDatesOp: FilterOperationType.Between,
  ticketTypeOp: FilterOperationType.In,
  fulfillerSellerUserIdOp: FilterOperationType.In,
  listingInsertByOp: FilterOperationType.In,
  isNoFulfillOp: FilterOperationType.Equals,
} satisfies SaleQueryOperations;

export const DefaultPurchaseQueryV2Operations = {
  ...DefaultEntityWithTicketsQueryOperations,
  ticketGroupIdsOp: FilterOperationType.In,
  purchaseMonthsOp: FilterOperationType.In,
  paymentMethodOp: FilterOperationType.In,
  paymentStatusOp: FilterOperationType.In,
  paymentDatesOp: FilterOperationType.Between,
  totalCostHighOp: FilterOperationType.LessThanOrEqual,
  totalCostLowOp: FilterOperationType.GreaterThanOrEqual,
  deliveryTypeOp: FilterOperationType.In,
  purchaseOrderStateOp: FilterOperationType.Equals,
  paymentMethodIdOp: FilterOperationType.In,
  paymentCurrencyCodesOp: FilterOperationType.In,
  isConsignmentOp: FilterOperationType.Equals,
} satisfies PurchaseQueryOperations;

export const EmptySaleQuery = {
  ...EmptyEntityWithTicketsQuery,
  saleStatus: null,
  saleStatuses: null,
  paymentState: null,
  marketplacePaymentState: null,
  saleDates: null,
  ticketType: null,
  soldOnMarketplace: null,
  fulfillerSellerUserId: null,
  lastSaleDates: null,
  noSaleDates: null,
  numberOfTicketsHeld: null,
  topLevelCategories: null,
  parentCategories: null,
  paymentDates: null,
  fulfillmentDates: null,
  cancellationDates: null,
  fulfillmentStates: null,
  paymentReceived: null,
  paymentReferenceId: null,
  listingInsertBy: null,
  isNoFulfill: null,
  viewMode: null,
} satisfies SaleQuery;

export const DefaultSaleQuery = {
  ...EmptySaleQuery,
  saleStatuses: [
    PointOfSaleSaleStatus.PendingConfirmation,
    PointOfSaleSaleStatus.PendingAllocation,
    PointOfSaleSaleStatus.PendingFulfillment,
    PointOfSaleSaleStatus.ProcessingFulfillment,
    PointOfSaleSaleStatus.Fulfilled,
    PointOfSaleSaleStatus.FulfillmentFailed,
    PointOfSaleSaleStatus.Hold,
    PointOfSaleSaleStatus.ProvisionalAllocation,
  ],
  sortBy: EventSort.Date,
  isSortDescending: false, // We want the nearest future event first
  eventTimeFrameFilter: EventTimeFrameFilter.Future,
} satisfies SaleQuery;

export const EmptySaleQueryV2 = {
  ...EmptySaleQuery,
  soldOnMarketplaces: null,
  marketplacePaymentStates: null,
  ticketTypes: null,
  fulfillerSellerUserIds: null,
  fulfillmentStates: null,
  listingInsertBys: null,
  operations: null,
  searchOptions: null,
} satisfies SaleQueryV2;

export const DefaultSaleQueryV2 = {
  ...EmptySaleQueryV2,
  ...DefaultSaleQuery,
  operations: DefaultSaleQueryOperations,
  searchOptions: {
    take: DEFAULT_SEARCH_V2_PAGE_SIZE,
    skip: null,
    includeAggregations: false,
    sort: [{ field: 'saleId', desc: false }],
    pitId: null,
    searchAfter: null,
  },
} satisfies SaleQueryV2;

export const EmptyListingQuery = {
  ...EmptyEntityWithTicketsQuery,
  pricerSellerUserId: null,
  isPredelivered: null,
  isListed: null,
  deliveryType: null,
  hasUnsoldTickets: null,
  eventHasUnsoldTickets: null,
  hasBroadcastError: null,
  isAutoPricingEnabled: null,
  isPricedAboveZero: null,
  eventUnpricedStatus: null,
  autoPricingResultTypes: null,
  isSeatingUnmapped: null,
  lastSaleDates: null,
  numberOfTicketsHeld: null,
  topLevelCategories: null,
  parentCategories: null,
  isAdminHold: null,
  isDuplicate: null,
  lastPriceUpdateDates: null,
  lastPriceNoUpdateDates: null,
  isPricedByMarketplace: null,
  isPendingProof: null,
  purchaseCancellationDates: null,
  purchaseCancelledOnly: null,
  isNoFulfill: null,
  ticketType: null,
  viewMode: null,
} satisfies ListingQuery;

export const DefaultListingQuery = {
  ...EmptyListingQuery,
  sortBy: EventSort.Date,
  isSortDescending: false, // We want the nearest future event first
  eventTimeFrameFilter: EventTimeFrameFilter.Future,
} satisfies ListingQuery;

export const EmptyListingQueryV2 = {
  ...EmptyListingQuery,
  pricerSellerUserIds: null,
  deliveryTypes: null,
  ticketTypes: null,
  splitTypes: null,
  eventUnpricedStatuses: null,
  operations: null,
  searchOptions: null,
} satisfies ListingQueryV2;

export const DefaultListingQueryV2 = {
  ...EmptyListingQueryV2,
  ...DefaultListingQuery,
  operations: DefaultListingQueryOperations,
  searchOptions: {
    take: DEFAULT_SEARCH_V2_PAGE_SIZE,
    skip: null,
    includeAggregations: false,
    sort: [{ field: 'listingId', desc: false }],
    pitId: null,
    searchAfter: null,
  },
} satisfies ListingQueryV2;

export const EmptyOnSaleEventQuery = {
  viewMode: null,
  onSaleDates: null,
  timezoneOffsetMins: null,
  countryCodes: null,
  performerIds: null,
  stateProvinceIds: null,
  cityIds: null,
  venueIds: null,
  topLevelCategories: null,
  parentCategories: null,
  searchText: null,
  eventIds: null,
  listingQtyRange: null,
  ticketQtyRange: null,
  priceMinRange: null,
  priceP25Range: null,
  priceP50Range: null,
  visitsL1HRange: null,
  visitsL24HRange: null,
  visitsL3DRange: null,
  visitsL7DRange: null,
  transL1HRange: null,
  transL24HRange: null,
  transL3DRange: null,
  transL7DRange: null,
} satisfies OnSaleEventQuery;

export const DefaultOnSaleEventQuery = {
  ...EmptyOnSaleEventQuery,
  viewMode: OnSaleEventViewMode.MetricView,
  onSaleDates: {
    start: getNextDateRange({ weeks: 2 }, new Date()).start.toISOString(),
    end: getNextDateRange({ weeks: 2 }, new Date()).end.toISOString(),
  } as DateTimeRangeWithRelative,
} satisfies OnSaleEventQuery;

export const EmptyPurchaseQuery = {
  ...EmptyEntityWithTicketsQuery,
  ticketGroupIds: null,
  purchaseMonths: null,
  paymentDates: null,
  paymentMethod: null,
  paymentStatus: null,
  totalCostHigh: null,
  totalCostLow: null,
  deliveryType: null,
  purchaseOrderState: null,
  isSortDescending: null,
  tagName: null,
  tagValue: null,
  entityIds: null,
  purchaseOrderCreatedBy: null,
  purchaseCommissionUserId: null,
  viewMode: null,
  currencyCode: null,
  paymentMethodId: null,
  internalNotesContains: null,
  internalNotesEquals: null,
  internalNotesNotContains: null,
  paymentCurrencyCodes: null,
  isConsignment: null,
} satisfies PurchaseOrderQuery;

export const DefaultPurchaseQuery = {
  ...EmptyPurchaseQuery,
  purchaseDates:
    (getUiDateTimeRangeFromPreset(
      DateRangePresetName.ThisYear,
      PurchaseDateRangePresetNames
    ) as DateTimeRangeWithRelative) ?? null,
  sortBy: EventSort.Date,
  purchaseOrderState: PurchaseOrderState.Active,
  isSortDescending: true,
} satisfies PurchaseOrderQuery;

export const DefaultPurchaseQueryWithLast365Days = {
  ...EmptyPurchaseQuery,
  purchaseDates: getLastDateRangeRelative({ years: 1 }),
  sortBy: EventSort.Date,
  purchaseOrderState: PurchaseOrderState.Active,
  isSortDescending: true,
} satisfies PurchaseOrderQuery;

export const EmptyPurchaseQueryV2 = {
  ...EmptyPurchaseQuery,
  deliveryTypes: null,
  paymentMethods: null,
  paymentMethodIds: null,
  paymentStatuses: null,
  operations: null,
  searchOptions: null,
} satisfies PurchaseOrderQueryV2;

export const DefaultPurchaseQueryV2 = {
  ...EmptyPurchaseQueryV2,
  ...DefaultPurchaseQuery,
  operations: DefaultPurchaseQueryV2Operations,
  searchOptions: {
    take: DEFAULT_SEARCH_V2_PAGE_SIZE,
    skip: null,
    includeAggregations: false,
    sort: [{ field: 'purchaseOrderId', desc: false }],
    pitId: null,
    searchAfter: null,
  },
} satisfies PurchaseOrderQueryV2;

export const DefaultPurchaseQueryV2WithLast365Days = {
  ...EmptyPurchaseQueryV2,
  ...DefaultPurchaseQueryWithLast365Days,
  operations: DefaultPurchaseQueryV2Operations,
  searchOptions: {
    take: DEFAULT_SEARCH_V2_PAGE_SIZE,
    skip: null,
    includeAggregations: false,
    sort: [{ field: 'purchaseOrderId', desc: false }],
    pitId: null,
    searchAfter: null,
  },
} satisfies PurchaseOrderQueryV2;

export const EmptyCommissionQuery = {
  eventTimeFrameFilter: null,
  eventDates: null,
  saleDates: null,
  saleStatuses: null,
  soldOnMarketplace: null,
  paymentDates: null,
  fulfillmentDates: null,
  purchaseCommissionUserId: null,
  isCommissionEligible: null,
  commissionEligibilityDates: null,
  timezoneOffsetMins: null,
  viewMode: null,
  isSortDescending: null,
} satisfies UIReportCommissionQuery;

export const DefaultCommissionQuery = {
  ...EmptyCommissionQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportCommissionQuery;

const EmptyReportEntityWithTicketsQuery = {
  performerIds: null,
  venueIds: null,
  countryCodes: null,
  stateProvinceIds: null,
  cityIds: null,
  purchaseOrderId: null,
  purchaseVendorId: null,
  vendorAccessControlProviderId: null,
  purchaseVendorAccountIds: null,
  purchaseVendorOrderId: null,
  purchaseBuyerId: null,
  purchaseOrderCreatedBy: null,
  purchaseCommissionUserId: null,
  purchaseDates: null,
  inHandDates: null,
  eventTimeFrameFilter: null,
  sections: null,
  rows: null,
  sectionContains: null,
  sectionEquals: null,
  sectionNotContains: null,
  rowContains: null,
  rowEquals: null,
  rowNotContains: null,
  isSeatSaver: null,
  eventDates: null,
  noSaleDates: null,
  tagName: null,
  tagValue: null,
  eventTagName: null,
  eventTagValue: null,
  lastSaleDates: null,
  numberOfTicketsHeld: null,
  topLevelCategories: null,
  parentCategories: null,
  internalNotes: null,
  internalNotesContains: null,
  internalNotesEquals: null,
  internalNotesNotContains: null,
  timezoneOffsetMins: null,
  currencyCode: null,
  currencyCodes: null,
  eventStatuses: null,
  eventHasUnsoldTickets: null,
  purchaseCancellationDates: null,
  isParking: null,
  listingIdContains: null,
  saleIdContains: null,
  marketplaceSaleIdContains: null,
  purchaseOrderIdContains: null,
  vendorOrderIdContains: null,
  eventNotReviewedSinceDate: null,
  eventUnpricedStatus: null,
} satisfies ReportEntityWithTicketsQuery;

export const EmptyReportV2SaleQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  isNoFulfill: null,
  saleDates: null,
  saleStatuses: null,
  soldOnMarketplace: null,
  cancellationDates: null,
  fulfillerSellerUserId: null,
  fulfillmentDates: null,
  paymentDates: null,
  marketplacePaymentState: null,
  viewMode: null,
  isSortDescending: null,
  paymentReferenceId: null,
  ticketType: null,
  listingInsertBy: null,
  dayOfWeek: null,
  quantityWasted: null,
  insertBy: null,
  listingNoteIds: null,
  pricerSellerUserId: null,
  originatedFromListingIdContains: null,
  seatContains: null,
  listingTagName: null,
  listingTagValue: null,
  purchaseOrderTagName: null,
  purchaseOrderTagValue: null,
  originatedFromListingTagName: null,
  originatedFromListingTagValue: null,
} satisfies UIReportV2SaleQuery;

export const DefaultReportV2SaleQuery = {
  ...EmptyReportV2SaleQuery,
  saleStatuses: [
    PointOfSaleSaleStatus.PendingConfirmation,
    PointOfSaleSaleStatus.PendingFulfillment,
    PointOfSaleSaleStatus.ProcessingFulfillment,
    PointOfSaleSaleStatus.Fulfilled,
    PointOfSaleSaleStatus.FulfillmentFailed,
    PointOfSaleSaleStatus.Hold,
    PointOfSaleSaleStatus.PendingAllocation,
    PointOfSaleSaleStatus.ProvisionalAllocation,
    PointOfSaleSaleStatus.PendingReshipment,
    PointOfSaleSaleStatus.Wasted,
  ],
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2SaleQuery;

export const EmptyReportV2SaleLineItemQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  isNoFulfill: null,
  saleDates: null,
  saleStatuses: null,
  soldOnMarketplace: null,
  cancellationDates: null,
  fulfillerSellerUserId: null,
  fulfillmentDates: null,
  paymentDates: null,
  marketplacePaymentState: null,
  viewMode: null,
  isSortDescending: null,
} satisfies UIReportV2SaleLineItemQuery;

export const DefaultReportV2SaleLineItemQuery = {
  ...EmptyReportV2SaleLineItemQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2SaleLineItemQuery;

export const EmptyReportV2SoldTicketQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  isNoFulfill: null,
  saleDates: null,
  saleStatuses: null,
  soldOnMarketplace: null,
  cancellationDates: null,
  fulfillerSellerUserId: null,
  fulfillmentDates: null,
  paymentDates: null,
  marketplacePaymentState: null,
  dayOfWeek: null,
  viewMode: null,
  isSortDescending: null,
} satisfies UIReportV2SoldTicketQuery;

export const DefaultReportV2SoldTicketQuery = {
  ...EmptyReportV2SoldTicketQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2SoldTicketQuery;

export const EmptyReportV2ListingQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  isPredelivered: null,
  isNoFulfill: null,
  lastPriceUpdateDates: null,
  isPricedByMarketplace: null,
  isAutoPricingEnabled: null,
  isListed: null,
  isMapped: null,
  isDuplicate: null,
  isSeatingUnmapped: null,
  isAdminHold: null,
  pricerSellerUserId: null,
  viewMode: null,
  isSortDescending: null,
  purchaseCancelledOnly: null,
  hasUnsoldTickets: null,
  autoPricingResultTypes: null,
  isPricedAboveZero: null,
  hasBroadcastError: null,
  isPendingProof: null,
  deliveryType: null,
  ticketType: null,
  purchaseInsertBy: null,
} satisfies UIReportV2ListingQuery;

export const DefaultReportV2ListingQuery = {
  ...EmptyReportV2ListingQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2ListingQuery;

export const EmptyReportV2PurchaseQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  paymentMethod: null,
  paymentStatus: null,
  purchaseOrderState: null,
  isSortDescending: null,
  purchaseOrderCreatedBy: null,
  viewMode: null,
  internalNotesContains: null,
  internalNotesEquals: null,
} satisfies UIReportV2PurchaseQuery;

export const DefaultReportV2PurchaseQuery = {
  ...EmptyReportV2PurchaseQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2PurchaseQuery;

export const EmptyReportV2PurchaseOrderLineItemQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  purchaseOrderCostTypeId: null,
  purchaseOrderState: null,
  purchasePaymentStatus: null,
  viewMode: null,
  isSortDescending: null,
} satisfies UIReportV2PurchaseOrderLineItemQuery;

export const DefaultReportV2PurchaseOrderLineItemQuery = {
  ...EmptyReportV2PurchaseOrderLineItemQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2PurchaseOrderLineItemQuery;

export const EmptyReportV2CostOfGoodsSoldQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  adjustmentsSinceDate: null,
  adjustmentType: null,
  isActive: null,
  viewMode: null,
  isSortDescending: null,
} satisfies UIReportV2CostOfGoodsSoldQuery;

export const DefaultReportV2CostOfGoodsSoldQuery = {
  ...EmptyReportV2CostOfGoodsSoldQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2CostOfGoodsSoldQuery;

export const EmptyReportV2SaleCostAdjustmentsQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  adjustmentsSinceDate: null,
  adjustmentType: null,
  viewMode: null,
  isSortDescending: null,
} satisfies UIReportV2SaleTotalCostAdjustmentsQuery;

export const DefaultReportV2SaleCostAdjustmentsQuery = {
  ...EmptyReportV2SaleCostAdjustmentsQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2SaleTotalCostAdjustmentsQuery;

export const EmptyReportV2TicketGroupQuery = {
  ...EmptyReportEntityWithTicketsQuery,
  viewMode: null,
  isSortDescending: null,
  paymentMethod: null,
  paymentStatus: null,
  isMapped: null,
  isConsignment: null,
  deliveryType: null,
  ticketStatuses: null,
  purchaseOrderState: PurchaseOrderState.Active,
} satisfies UIReportV2TicketGroupQuery;

export const DefaultReportV2TicketGroupQuery = {
  ...EmptyReportV2TicketGroupQuery,
  eventTimeFrameFilter: null, // all
  isSortDescending: true,
} satisfies UIReportV2TicketGroupQuery;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FilterQueryType = Record<string, any>;

export enum EventUiSort {
  DateDesc = 'Sort By: Newest to Oldest',
  DateAsc = 'Sort By: Oldest to Newest',
  SortAtoZ = 'Sort By: A to Z',
  SortZtoA = 'Sort By: Z to A',
}

export const getEventUiSort = (
  sortBy: EventSort | null,
  isSortDescending: boolean | null
) => {
  switch (sortBy) {
    case EventSort.MainDisplay:
      return isSortDescending ? EventUiSort.SortZtoA : EventUiSort.SortAtoZ;
    case EventSort.Date:
    default:
      return isSortDescending ? EventUiSort.DateDesc : EventUiSort.DateAsc;
  }
};

export const getEventSort = (
  sort: EventUiSort | null
): [EventSort, boolean] => {
  switch (sort) {
    case EventUiSort.SortAtoZ:
      return [EventSort.MainDisplay, false];
    case EventUiSort.SortZtoA:
      return [EventSort.MainDisplay, true];
    case EventUiSort.DateAsc:
      return [EventSort.Date, false];
    case EventUiSort.DateDesc:
    default:
      return [EventSort.Date, true];
  }
};

export const getAppliedFilterCounts = (
  currentFilter: FilterQueryType,
  initialFilter: FilterQueryType,
  ignoreKeys: string[] = []
) => {
  const initKeys = Object.keys(initialFilter);

  let count = 0;
  for (let i = 0; i < initKeys.length; i++) {
    if (ignoreKeys.includes(initKeys[i])) {
      continue;
    }
    const initValue = initialFilter[initKeys[i]];
    const curValue = currentFilter[initKeys[i]];
    if (!isEqual(curValue, initValue) && initValue !== undefined) {
      count++;
    }
  }

  return count;
};

const isFilterApplied = (
  currentValue: unknown,
  initialValue: unknown,
  returnAllNonEmptyFilters = false
): boolean => {
  // If we want to pick non-empty values, then we only need to check if the current value is not null
  if (returnAllNonEmptyFilters) {
    return currentValue != null;
  }
  // No need to differentiate between null and undefined
  if (currentValue == null && initialValue == null) {
    return false;
  }
  return !isEqual(currentValue, initialValue);
};

/**
 * Returns the filters that match with:
 * * not undefined (null is a valid value)
 * * not in initial/default query
 * * values is different from initial/default query
 * * not in ignored Ids list
 * @param currentFilterQuery
 * @param initialFilterQuery
 * @param ignoreFilterIds Not shown in the UI
 * @param filtersGroups
 * @param returnAllNonEmptyFilters If true, then we only check if the current value is not null
 */
export const getAppliedFilters = (
  currentFilterQuery: FilterQueryType,
  initialFilterQuery: FilterQueryType,
  ignoreFilterIds: string[] = [],
  filtersGroups: FilterToolbarGroup[],
  returnAllNonEmptyFilters = false
): FilterToolbarItemId[] => {
  // Sometimes, initialQuery has only default values, not the full query,
  // and the current query has more keys, so merge all together to make
  // the comparisons
  const initialKeys = [
    ...new Set([
      ...Object.keys(initialFilterQuery),
      ...Object.keys(currentFilterQuery),
    ]),
  ];

  const filtersMap: Partial<Record<FilterToolbarItemId, FilterToolbarItem>> =
    {};

  filtersGroups.forEach((filterGroup) => {
    filterGroup.items.forEach((filterItem) => {
      filtersMap[filterItem.filterId] = filterItem;
    });
  });

  const appliedFilters = new Set();
  for (let i = 0; i < initialKeys.length; i++) {
    if (ignoreFilterIds.includes(initialKeys[i])) {
      continue;
    }
    const filterId = initialKeys[i] as FilterToolbarItemId;

    // Some filters rely on more than one key of the filterQuery, so check if
    // the declared keys in the filter are used.
    const filterKeys = filtersMap[filterId]?.filterQueryKeys ?? [];
    const someApplied = filterKeys.some((fId) => {
      const initialValue = initialFilterQuery[fId];
      const currentValue = currentFilterQuery[fId];
      return isFilterApplied(
        currentValue,
        initialValue,
        returnAllNonEmptyFilters
      );
    });
    if (someApplied) {
      appliedFilters.add(filterId);
    }
  }

  return [...appliedFilters] as FilterToolbarItemId[];
};

export const MainFilterQueryParam = 'mainFilter';

const removeEntityIdsIfHasSearchId = (copy: FilterQueryType) => {
  const entityQuery = copy as EntityWithTicketsQuery;
  if (entityQuery.searchConfigId) {
    // If we have the search id, then we clear-out everything since only the search config id matter
    return {
      ...entityQuery,
      entityIds: null,
    } as EntityWithTicketsQuery;
  }

  return copy;
};

export const deleteDefaultFields = (
  baseQuery: FilterQueryType,
  query: FilterQueryType | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryValueTransformToUrl?: (key: FilterToolbarItemId, value: any) => any
) => {
  if (!query) {
    return null;
  }

  let copy = Object.entries(baseQuery).reduce((result, baseEntry) => {
    const curValue = query[baseEntry[0]];
    if (!isEqual(baseEntry[1], curValue)) {
      result[baseEntry[0]] = queryValueTransformToUrl
        ? queryValueTransformToUrl(
            baseEntry[0] as FilterToolbarItemId,
            curValue
          )
        : curValue;
    }

    return result;
  }, {} as FilterQueryType);

  copy = removeEntityIdsIfHasSearchId(copy);

  return copy;
};

export const queryValueTransformFromUrlBase = (
  key: string,
  value: string | object
) => {
  if (!value) {
    return value;
  }

  if (typeof value === 'object') {
    const obj = value as DateTimeRangeWithRelative;
    if (obj.relativeEndSecs || obj.relativeStartSecs) {
      return { ...obj, roundingMode: obj.roundingMode };
    }
  }

  return value;
};

export const mapToBaseQuery = <TQuery extends FilterQueryType>(
  baseQuery: TQuery,
  query: TQuery | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryValueTransformFromUrl?: (key: string, value: any) => any
) => {
  if (!query) {
    return null;
  }

  let copy = Object.entries(baseQuery).reduce((result, baseEntry) => {
    const filterId = baseEntry[0];
    const queryValue = query?.[filterId];
    result[filterId] = queryValueTransformFromUrlBase(
      filterId,
      queryValue === undefined
        ? baseEntry[1] // Only use baseEntry's value if the queryValue is undefined (null is a valid value)
        : queryValueTransformFromUrl
          ? queryValueTransformFromUrl(filterId, queryValue)
          : queryValue
    );
    return result;
  }, {} as FilterQueryType);

  copy = removeEntityIdsIfHasSearchId(copy);

  return copy as TQuery;
};

export const isQueryEqual = <TQuery extends FilterQueryType>(
  baseQuery: TQuery,
  query1: TQuery | null,
  query2: TQuery | null
) => {
  const baseKeys = Object.keys(baseQuery);

  for (const i in baseKeys) {
    const v1 = query1?.[baseKeys[i]];
    const v2 = query2?.[baseKeys[i]];

    if (!isEqual(v1, v2)) {
      return false;
    }
  }

  return true;
};
export const getQueryFromUrl = <TQuery extends FilterQueryType>(
  baseQuery: TQuery,
  urlSearchString: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryValueTransformFromUrl?: (key: string, value: any) => any
): TQuery | null => {
  if (urlSearchString) {
    const searchParams = new URLSearchParams(urlSearchString);
    const filterQueryParam = searchParams.get(MainFilterQueryParam);
    if (filterQueryParam) {
      try {
        const obj = JSON.parse(filterQueryParam);
        if (obj) {
          return mapToBaseQuery(baseQuery, obj, queryValueTransformFromUrl);
        }
      } catch (e) {
        console.warn(`Unable to parse as a filter obj: ${filterQueryParam}`);
        return null;
      }
    }
  }

  return baseQuery;
};

export const getUrlFilterQueryParam = <TQuery extends FilterQueryType>(
  baseQuery: TQuery,
  query: TQuery | null,
  baseSearchParams?: URLSearchParams,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryValueTransformToUrl?: (key: FilterToolbarItemId, value: any) => any
) => {
  const searchParams = baseSearchParams || new URLSearchParams();
  searchParams.set(
    MainFilterQueryParam,
    JSON.stringify(
      deleteDefaultFields(baseQuery, query, queryValueTransformToUrl)
    )
  );

  return `?${searchParams}`;
};

export const isFilterQueryParamEmpty = (searchParams?: string): boolean => {
  if (!searchParams) {
    return true;
  }
  try {
    const urlSearchParams = new URLSearchParams(searchParams);
    const mainFilters = urlSearchParams.get(MainFilterQueryParam);
    return !mainFilters || mainFilters === '{}';
  } catch (e) {
    return false;
  }
};

export const removeFilters = <TQuery extends FilterQueryType>(
  urlSearchParams: string | undefined,
  filtersKeyToRemove: (keyof TQuery)[]
): string => {
  if (!urlSearchParams || isFilterQueryParamEmpty(urlSearchParams)) {
    return urlSearchParams ?? '';
  }

  try {
    const searchFilterParams = new URLSearchParams(urlSearchParams);
    const mainFilter = searchFilterParams.get(MainFilterQueryParam)!;
    const filters = JSON.parse(mainFilter) as TQuery;
    filtersKeyToRemove.forEach((filterKey) => {
      delete filters[filterKey];
    });

    searchFilterParams.set(MainFilterQueryParam, JSON.stringify(filters));
    return searchFilterParams.toString();
  } catch (e) {
    return urlSearchParams ?? '';
  }
};

export const getLastNDaysSaleQuery = (days: number): SaleQuery => {
  const today = new Date();
  const lastNDays = new Date();
  lastNDays.setDate(today.getDate() - days);
  return {
    ...DefaultSaleQuery,
    eventTimeFrameFilter: null, // all
    // cap the event time filter to filter with N look-back instead of all - which is perf hit
    eventDates: {
      start: lastNDays.toISOString(),
      end: null,
    } as DateTimeRangeWithRelative,
    saleDates: {
      start: lastNDays.toISOString(),
      end: today.toISOString(),
    } as DateTimeRangeWithRelative,
  };
};

export const getPurchaseOrderIdSaleQuery = (
  purchaseOrderId: number,
  eventOrMappingIds: string[] | null
): SaleQuery => {
  return {
    ...DefaultSaleQuery,
    purchaseOrderId,
    eventTimeFrameFilter: null, // we want all
    eventOrMappingIds,
  };
};

export const getPurchaseOrderIdListingQuery = (
  purchaseOrderId: number,
  eventOrMappingIds: string[] | null
): ListingQuery => {
  return {
    ...DefaultListingQuery,
    purchaseOrderId,
    eventTimeFrameFilter: null,
    eventOrMappingIds,
  };
};

export const getSaleStatusesSaleQuery = (
  saleStatuses: PointOfSaleSaleStatus[]
): SaleQuery => {
  return {
    ...DefaultSaleQuery,
    saleStatuses,
  };
};

export enum YesNoEnum {
  Yes = 'Yes',
  No = 'No',
}

export enum LastReviewedSinceDate {
  LastThreeDays = 'Last 3 Days',
  LastSevenDays = 'Last 7 Days',
  LastTwoWeeks = 'Last 2 weeks',
  LastThirtyDays = 'Last 30 Days',
}

export enum TextFilterEnum {
  Contains = 'Contains',
  Equals = 'Equals',
  NotContains = 'NotContains',
}

export const ToEventMappingStateFilter = (
  isMapped: boolean | null | undefined,
  isMappingRejected: boolean | null | undefined
) => {
  if (isMapped == null) {
    return EventMappingStateFilter.All;
  }
  if (isMapped === true) {
    return EventMappingStateFilter.Mapped;
  }

  // isMapped === false
  return isMappingRejected === true
    ? EventMappingStateFilter.Rejected
    : EventMappingStateFilter.Pending;
};

export const FromEventMappingStateFilter = (
  eventMappingStateFilter: EventMappingStateFilter
): [boolean | null, boolean | null] => {
  // [isMapped, isMappingRejected]
  switch (eventMappingStateFilter) {
    case EventMappingStateFilter.All:
      return [null, null];
    case EventMappingStateFilter.Mapped:
      return [true, null];
    case EventMappingStateFilter.Pending:
      return [false, false];
    case EventMappingStateFilter.Rejected:
      return [false, true];
  }
};

export const ToYesNoEnum = (value: boolean | null | undefined) => {
  return value == null ? null : value ? YesNoEnum.Yes : YesNoEnum.No;
};

export const FromYesNoEnum = (yesNoEnumValue: YesNoEnum | null) => {
  return yesNoEnumValue === YesNoEnum.Yes
    ? true
    : yesNoEnumValue === YesNoEnum.No
      ? false
      : null;
};

const EVENT_UNPRICED_STATUS_TO_NUMBER: Record<EventUnpricedStatus, number> = {
  [EventUnpricedStatus.AllUnpriced]: 1,
  [EventUnpricedStatus.NoneUnpriced]: 2,
  [EventUnpricedStatus.AnyUnpriced]: 3,
};

const NUMBER_TO_EVENT_UNPRICED_STATUS: Record<number, EventUnpricedStatus> = {
  1: EventUnpricedStatus.AllUnpriced,
  2: EventUnpricedStatus.NoneUnpriced,
  3: EventUnpricedStatus.AnyUnpriced,
};

export const ToEventUnpricedStatusEnum = (value: number | null | undefined) => {
  return value == null ? null : NUMBER_TO_EVENT_UNPRICED_STATUS[value] || null;
};

export const FromEventUnpricedStatusEnum = (
  eventUnpricedStatus: EventUnpricedStatus | null
) => {
  return eventUnpricedStatus == null
    ? null
    : EVENT_UNPRICED_STATUS_TO_NUMBER[eventUnpricedStatus] || null;
};

/**
 * Filters the `catalogResults` using the given date range.
 *
 * This mutates the original `catalogResults`.
 * @param catalogResults
 * @param dateRangeStrings
 * @returns
 */
export function filterCatalogResultsByEventDate(
  catalogResults: CatalogResults,
  dateRangeStrings: DateTimeRange
) {
  const dateRange = {
    start: dateRangeStrings?.start ? new Date(dateRangeStrings.start) : null,
    end: dateRangeStrings?.end ? new Date(dateRangeStrings.end) : null,
  };

  const filteredEvents: { [key: string]: EventWithData } = {};
  for (const id in catalogResults.events) {
    const { event } = catalogResults.events[id];
    if (event.dates.start != null) {
      const eventDate = new Date(event.dates.start);
      if (
        (dateRange.start == null || eventDate >= dateRange.start) &&
        (dateRange.end == null || eventDate <= dateRange.end)
      ) {
        filteredEvents[id] = catalogResults.events[id];
      }
    }
  }
  catalogResults.events = filteredEvents;
  return catalogResults;
}

export const isFilterUnchanged = <
  T extends ListingQuery | SaleQuery | PurchaseOrderQuery,
>(
  query: T,
  emptyQuery: T
) => {
  const {
    entityIds,
    performerIds,
    venueIds,
    searchText,
    sortBy,
    isSortDescending,
    searchConfigId,
    viewMode,
    ...rest1
  } = query;

  const {
    entityIds: x1,
    performerIds: x2,
    venueIds: x3,
    searchText: x4,
    sortBy: x5,
    isSortDescending: x6,
    searchConfigId: x7,
    viewMode: x8,
    ...rest2
  } = emptyQuery;

  return Object.entries(rest1).length === 0 || isEqual(rest1, rest2);
};

export const isValidFilterQuery = <T>(query: string): T | undefined => {
  try {
    return JSON.parse(query) as T;
  } catch (e) {
    return undefined;
  }
};

export const mapEntityWithTicketsQueryToEntityWithTicketsQueryV2 = (
  query: EntityWithTicketsQuery
): EntityWithTicketsQuery => {
  return {
    ...EmptyEntityWithTicketsQuery,
    ...query,
    purchaseVendorIds: query.purchaseVendorId ? [query.purchaseVendorId] : null,
    purchaseBuyerIds: query.purchaseBuyerId ? [query.purchaseBuyerId] : null,
    purchaseOrderCreatedBys: query.purchaseOrderCreatedBy
      ? [query.purchaseOrderCreatedBy]
      : null,
    purchaseCommissionUserIds: query.purchaseCommissionUserId
      ? [query.purchaseCommissionUserId]
      : null,
  };
};

export const mapListingQueryToListingQueryV2 = (
  query: ListingQuery | ListingQueryV2
): ListingQueryV2 => {
  return {
    ...EmptyListingQueryV2,
    ...mapEntityWithTicketsQueryToEntityWithTicketsQueryV2(query),
    currencyCodes: query.currencyCode
      ? [query.currencyCode]
      : query.currencyCodes,
    pricerSellerUserIds: query.pricerSellerUserId
      ? [query.pricerSellerUserId]
      : null,
    deliveryTypes: query.deliveryType ? [query.deliveryType] : null,
    ticketTypes: query.ticketType ? [query.ticketType] : null,
    eventUnpricedStatuses: query.eventUnpricedStatus
      ? [query.eventUnpricedStatus]
      : null,
    operations:
      (query as ListingQueryV2).operations ?? DefaultListingQueryOperations,
  };
};

export const mapSaleQueryToSaleQueryV2 = (
  query: SaleQuery | SaleQueryV2
): SaleQueryV2 => {
  return {
    ...EmptySaleQueryV2,
    ...mapEntityWithTicketsQueryToEntityWithTicketsQueryV2(query),
    currencyCodes: query.currencyCode
      ? [query.currencyCode]
      : query.currencyCodes,
    soldOnMarketplaces: query.soldOnMarketplace
      ? [query.soldOnMarketplace]
      : null,
    marketplacePaymentStates: query.marketplacePaymentState
      ? [query.marketplacePaymentState]
      : null,
    ticketTypes: query.ticketType ? [query.ticketType] : null,
    fulfillerSellerUserIds: query.fulfillerSellerUserId
      ? [query.fulfillerSellerUserId]
      : null,
    listingInsertBys: query.listingInsertBy ? [query.listingInsertBy] : null,
    operations: (query as SaleQueryV2).operations ?? DefaultSaleQueryOperations,
  };
};

export const mapPurchaseQueryToPurchaseQueryV2 = (
  query: PurchaseOrderQuery | PurchaseOrderQueryV2
): PurchaseOrderQueryV2 => {
  return {
    ...EmptyPurchaseQueryV2,
    ...mapEntityWithTicketsQueryToEntityWithTicketsQueryV2(query),
    currencyCodes: query.currencyCode
      ? [query.currencyCode]
      : query.currencyCodes,
    deliveryTypes: query.deliveryType ? [query.deliveryType] : null,
    paymentMethods: query.paymentMethod ? [query.paymentMethod] : null,
    paymentMethodIds: query.paymentMethodId ? [query.paymentMethodId] : null,
    paymentStatuses: query.paymentStatus ? [query.paymentStatus] : null,
    operations:
      (query as PurchaseOrderQueryV2).operations ??
      DefaultPurchaseQueryV2Operations,
  };
};

/**
 * Hydrates the filter query with the legacy event ID urls
 * @param viagogoVirtualIds
 * @param filterQuery
 * @returns
 */
export const hydrateFiltersWithLegacyEventIds = <
  T extends EntityWithTicketsQuery,
>(
  viagogoVirtualIds: string[],
  filterQuery: T
) => {
  // Is is to support the legacy event ID urls
  return {
    ...filterQuery,
    oldPosEventIds: viagogoVirtualIds
      .filter((id) => id.startsWith('old:'))
      .map((id) => id.substring(4)),
    eventOrMappingIds: viagogoVirtualIds.filter((id) => !id.startsWith('old:')),
  };
};
